/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Container from "../../components/Container";
import Button from "../../components/ui/Button";
import ButtonGroup from "../../components/ui/ButtonGroup";
import { ROUTES } from "../../constants/constants";
import FormGroup from "../../components/ui/form/FormGroup";
import { useForm } from "react-hook-form";
import FormError from "../../components/ui/form/FormError";
import PasswordInput from "../../components/ui/form/PasswordInput";
import Form from "../../components/ui/form/Form";
import FormFieldsWrapper from "../../components/ui/form/FormFieldsWrapper";
import NavigationHeader from "../../components/NavigationHeader";
import { useClientPasswordChange } from "../../lib/react-query/useClientPassword";
import { useToast } from "../../components/ui/Toast";
import { extractErrorMessage, extractFirstFieldError } from "../../utils/api/apiErrors";

const StyledClientPasswordChangeForm = styled.div`
  display: grid;
  gap: 24px;
  place-items: start;
  padding: 24px;
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  border-radius: 1rem;
`;

const ClientPasswordChange = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { mutate, isPending } = useClientPasswordChange();
  const {
    handleSubmit,
    watch,
    control,
    formState: { errors },
    setError,
  } = useForm({
    defaultValues: {
      old_password: "",
      password: "",
      password_confirm: "",
    },
  });

  const handleCancel = (e) => {
    e.preventDefault();
    navigate(ROUTES.CLIENT_PROFILE);
  };

  const onSubmit = (data) => {
    const { old_password, password } = data;

    mutate(
      { old_password, password },
      {
        onSuccess: () => {
          navigate(ROUTES.CLIENT_PROFILE);
          toast({
            title: "success",
            description: "Your password has been successfully updated",
            variant: "success",
          });
        },
        onError: (error) => handleError(error, setError),
      }
    );
  };

  const handleError = (error, setError) => {
    if (!setError) return;

    const passwordError = extractFirstFieldError(error, "password");

    if (passwordError) {
      setError("password", { message: passwordError });
    } else {
      setError("root", { message: extractErrorMessage(error, "An unexpected error occurred") });
    }
  };

  return (
    <Container>
      <NavigationHeader title="my_profile" back={ROUTES.CLIENT_PROFILE} />
      <StyledClientPasswordChangeForm>
        <Form>
          <FormFieldsWrapper>
            <FormGroup>
              <PasswordInput
                control={control}
                name="old_password"
                label="Old password"
                placeholder="Enter your old password"
                error={errors.old_password}
                required={true}
              />
              {errors.old_password && <FormError name="old_password">{errors.old_password.message}</FormError>}
            </FormGroup>
            <FormGroup>
              <PasswordInput
                control={control}
                name="password"
                label="New password"
                placeholder="Enter your new password"
                error={errors.password}
                required={true}
              />
              {errors.password && <FormError name="password">{errors.password.message}</FormError>}
            </FormGroup>
            <FormGroup>
              <PasswordInput
                control={control}
                name="password_confirm"
                label="Repeat new password"
                placeholder="Enter your new password again"
                validationRules={{ validate: (value) => value === watch("password") || "Passwords do not match" }}
                error={errors.password_confirm}
                required={true}
              />
              {errors.password_confirm && <FormError name="password_confirm">{errors.password_confirm.message}</FormError>}
            </FormGroup>
          </FormFieldsWrapper>
          <ButtonGroup gap="large">
            <Button variant="outline" title="Cancel changes" onClick={handleCancel} />
            <Button
              variant="primary"
              title="Change password"
              onClick={handleSubmit(onSubmit)}
              loading={isPending}
              fullWidth
            />
          </ButtonGroup>
          {errors.root && <FormError>{errors.root.message}</FormError>}
        </Form>
      </StyledClientPasswordChangeForm>
    </Container>
  );
};

export default ClientPasswordChange;
